import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { DIST_PORTFOLIOS, distLabel } from "./distConfig";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface Fund { id: string; fund_name: string; fe_fund_name: string; fund_manager: string | null; fund_class: string | null; isin: string | null; }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; }
interface SectorMapping { fund_id: string; sector_id: string; weighting: number; }
interface FundYield { fund_id: string; yield_pct: number | null; }

function PortfolioCalculator({ portfolio }: { portfolio: number }) {
  const qc = useQueryClient();
  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors-calc"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("id, name, sector_group, display_order").order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });
  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds-calc"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, fund_name, fe_fund_name, fund_manager, fund_class, isin").eq("is_current", true).order("fund_name");
      if (error) throw error;
      return data as Fund[];
    },
  });
  const { data: feYields } = useQuery({
    queryKey: ["fe-asset-breakdown-yields"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_asset_breakdown").select("isin, yield_pct, as_of_date").order("as_of_date", { ascending: false });
      if (error) throw error;
      const m = new Map<string, number>();
      for (const r of (data || []) as { isin: string; yield_pct: number | null }[]) {
        if (!m.has(r.isin) && r.yield_pct != null) m.set(r.isin.toUpperCase(), Number(r.yield_pct));
      }
      return m;
    },
  });
  const { data: weights } = useQuery({
    queryKey: ["ba-dist-fund-weights-calc", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_dist_cbam_fund_weights")
        .select("fund_id, portfolio, weight_pct, effective_date")
        .eq("portfolio", portfolio)
        .order("effective_date", { ascending: false });
      if (error) throw error;
      // Keep only the latest effective_date row per fund_id — an exit is an explicit
      // weight_pct = 0 row at the exit date, so older rows are past holdings, not current ones.
      const latest = new Map<string, FundWeight>();
      for (const row of (data || []) as (FundWeight & { effective_date: string })[]) {
        if (!latest.has(row.fund_id)) latest.set(row.fund_id, row);
      }
      return Array.from(latest.values()) as FundWeight[];
    },
  });
  const { data: mappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings-calc"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_fund_sector_mappings").select("fund_id, sector_id, weighting");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });
  const { data: yields } = useQuery({
    queryKey: ["ba-dist-fund-yields"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_dist_fund_yields" as any).select("fund_id, yield_pct");
      if (error) throw error;
      return ((data || []) as unknown) as FundYield[];
    },
  });

  const [hypothetical, setHypothetical] = useState<Record<string, number>>({});
  const [yieldEdits, setYieldEdits] = useState<Record<string, string>>({});
  useEffect(() => {
    if (weights) {
      const init: Record<string, number> = {};
      for (const w of weights) init[w.fund_id] = Number(w.weight_pct);
      setHypothetical(init);
    }
  }, [weights]);

  const yieldMap = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const y of yields || []) m.set(y.fund_id, y.yield_pct == null ? null : Number(y.yield_pct));
    return m;
  }, [yields]);

  const fundsInPortfolio = useMemo(() => {
    if (!funds || !weights) return [];
    const ids = new Set(weights.filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id));
    return funds.filter((f) => ids.has(f.id) || (hypothetical[f.id] || 0) > 0);
  }, [funds, weights, hypothetical]);

  const totalHypothetical = useMemo(
    () => fundsInPortfolio.reduce((acc, f) => acc + (hypothetical[f.id] || 0), 0),
    [fundsInPortfolio, hypothetical]
  );

  const feYieldForFund = (fundId: string): number | null => {
    const f = funds?.find((x) => x.id === fundId);
    if (!f?.isin) return null;
    return feYields?.get(f.isin.toUpperCase()) ?? null;
  };

  const effectiveYield = (fundId: string): number | null => {
    if (yieldEdits[fundId] !== undefined && yieldEdits[fundId] !== "") {
      const v = Number(yieldEdits[fundId]);
      return Number.isNaN(v) ? null : v;
    }
    if (yieldEdits[fundId] === "") return null;
    const manual = yieldMap.get(fundId);
    if (manual != null) return manual;
    return feYieldForFund(fundId);
  };

  const weightedYield = (wAt: (fundId: string) => number): number | null => {
    let num = 0, den = 0;
    for (const f of fundsInPortfolio) {
      const w = wAt(f.id);
      const y = effectiveYield(f.id);
      if (y == null || !w) continue;
      num += w * y;
      den += w;
    }
    return den > 0 ? num / den : null;
  };

  const currentYield = useMemo(
    () => weightedYield((id) => Number(weights?.find((w) => w.fund_id === id)?.weight_pct || 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weights, fundsInPortfolio, yieldMap, yieldEdits]
  );
  const hypotheticalYield = useMemo(
    () => weightedYield((id) => hypothetical[id] || 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hypothetical, fundsInPortfolio, yieldMap, yieldEdits]
  );

  const saveYieldMutation = useMutation({
    mutationFn: async (payload: { fund_id: string; yield_pct: number | null }) => {
      const { error } = await supabase.from("ba_dist_fund_yields" as any).upsert(payload, { onConflict: "fund_id" });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["ba-dist-fund-yields"] });
      setYieldEdits((prev) => { const n = { ...prev }; delete n[vars.fund_id]; return n; });
      toast.success("Yield saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const hypotheticalBySector = useMemo(() => {
    const out = new Map<string, number>();
    if (!mappings) return out;
    for (const f of fundsInPortfolio) {
      const fw = hypothetical[f.id] || 0;
      if (!fw) continue;
      for (const m of mappings.filter((m) => m.fund_id === f.id)) {
        out.set(m.sector_id, (out.get(m.sector_id) || 0) + fw * Number(m.weighting));
      }
    }
    return out;
  }, [fundsInPortfolio, hypothetical, mappings]);

  const resetToCurrent = () => {
    if (weights) {
      const init: Record<string, number> = {};
      for (const w of weights) init[w.fund_id] = Number(w.weight_pct);
      setHypothetical(init);
    }
    setYieldEdits({});
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Hypothetical Fund Weights — {distLabel(portfolio)}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={Math.abs(totalHypothetical - 100) < 0.05 ? "default" : "destructive"}>Total: {totalHypothetical.toFixed(2)}%</Badge>
            <Button variant="outline" size="sm" onClick={resetToCurrent}><RotateCcw className="h-3 w-3 mr-1" /> Reset</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto max-h-[600px] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 p-3 border-b bg-muted/30">
            <div className="text-xs">
              <div className="text-muted-foreground">Current weighted yield</div>
              <div className="font-mono text-base font-semibold">{currentYield != null ? `${currentYield.toFixed(2)}%` : "—"}</div>
            </div>
            <div className="text-xs">
              <div className="text-muted-foreground">Hypothetical weighted yield</div>
              <div className="font-mono text-base font-semibold">
                {hypotheticalYield != null ? `${hypotheticalYield.toFixed(2)}%` : "—"}
                {currentYield != null && hypotheticalYield != null && (
                  <span className={cn("ml-2 text-xs", hypotheticalYield - currentYield >= 0 ? "text-green-600" : "text-red-600")}>
                    ({hypotheticalYield - currentYield >= 0 ? "+" : ""}{(hypotheticalYield - currentYield).toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr className="border-b">
                <th className="text-left p-3 font-medium text-muted-foreground">Fund House</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-24">Current %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-28">Hypothetical %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-36">Yield %</th>
              </tr>
            </thead>
            <tbody>
              {fundsInPortfolio.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-xs">No funds with weight in {distLabel(portfolio)}.</td></tr>
              )}
              {fundsInPortfolio.map((f, idx) => {
                const current = weights?.find((w) => w.fund_id === f.id)?.weight_pct || 0;
                const storedYield = yieldMap.get(f.id);
                const feYield = feYieldForFund(f.id);
                const displayStored = storedYield != null ? storedYield : feYield;
                const yieldVal = yieldEdits[f.id] !== undefined ? yieldEdits[f.id] : (displayStored != null ? String(displayStored) : "");
                const dirty = yieldEdits[f.id] !== undefined && String(storedYield ?? "") !== yieldEdits[f.id];
                return (
                  <tr key={f.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs text-muted-foreground">{f.fund_manager || "—"}</td>
                    <td className="p-3 text-xs font-medium">{f.fund_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{f.fund_class || "—"}</td>
                    <td className="p-3 text-right text-xs font-mono text-muted-foreground">{Number(current).toFixed(2)}</td>
                    <td className="p-2 text-right">
                      <Input type="number" step="0.01"
                        value={hypothetical[f.id] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                          setHypothetical((prev) => ({ ...prev, [f.id]: isNaN(v) ? 0 : v }));
                        }}
                        className="h-8 text-xs text-right font-mono" />
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1 justify-end">
                        <Input type="number" step="0.01"
                          value={yieldVal}
                          placeholder="—"
                          onChange={(e) => setYieldEdits((prev) => ({ ...prev, [f.id]: e.target.value }))}
                          className="h-8 text-xs text-right font-mono w-20" />
                        {dirty && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                            onClick={() => {
                              const raw = yieldEdits[f.id];
                              const num = raw === "" ? null : Number(raw);
                              saveYieldMutation.mutate({ fund_id: f.id, yield_pct: Number.isNaN(num as number) ? null : num });
                            }}>
                            <Save className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>


      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">Resulting Sector Allocation</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr className="border-b">
                <th className="text-left p-3 font-medium text-muted-foreground">CBAM Sector</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Hypothetical %</th>
              </tr>
            </thead>
            <tbody>
              {sectors?.map((s, idx) => {
                const hyp = hypotheticalBySector.get(s.id) || 0;
                return (
                  <tr key={s.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs font-medium">{s.name}</td>
                    <td className="p-3 text-right text-xs font-mono">
                      {hyp > 0 ? hyp.toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 bg-muted/40 font-semibold">
                <td className="p-3 text-xs">Total</td>
                <td className="p-3 text-right text-xs font-mono">
                  {Array.from(hypotheticalBySector.values()).reduce((a, b) => a + b, 0).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function DistHypotheticalCalculator() {
  const [portfolio, setPortfolio] = useState("2");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Hypothetical Asset Allocation Calculator</h1>
        <p className="text-sm text-muted-foreground">Adjust fund weights to model resulting CBAM sector allocations.</p>
      </div>
      <Tabs value={portfolio} onValueChange={setPortfolio}>
        <TabsList>
          {DIST_PORTFOLIOS.map((p) => (<TabsTrigger key={p} value={String(p)}>{distLabel(p)}</TabsTrigger>))}
        </TabsList>
        {DIST_PORTFOLIOS.map((p) => (
          <TabsContent key={p} value={String(p)} className="mt-4">
            {portfolio === String(p) && <PortfolioCalculator portfolio={p} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
